import clsx from 'clsx';
import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { SINGLE_VALUE_ARIA_LABEL } from './defaults.js';
import { useSingleValueConfig } from './hooks/use-single-value-config.js';
import { container } from './SingleValue.sty.js';
import { Sparkline } from './slots/Sparkline.js';
import { Trend } from './slots/Trend.js';
import type { SingleValueRef } from './types/single-value-ref.js';
import type { SingleValueProps } from './types/single-value.js';
import { iterateSlots } from './utils/iterate-slots.js';
import { normalizeConfig } from './utils/single-value-config.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { SingleValueRenderer } from '../core/components/single-value-renderer/SingleValueRenderer.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ChartType } from '../core/types/chart-types.js';

/**
 * Single Value component
 */
const SingleValueRoot = /* @__PURE__ */ forwardRef<
  SingleValueRef,
  PropsWithChildren<SingleValueProps>
>((props, forwardedRef) => {
  const {
    data,
    prefixIcon,
    children,
    loading = false,
    style: customerStyle,
    className: customerClassNames,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    applyThresholdBackground,
    ...restProps
  } = props;
  const { slots, sparklineChildren } = iterateSlots(children);

  const { emptyState, errorState } = getChartStatesTemplates(children);

  const config = useSingleValueConfig(restProps, slots);

  const sparklineProps = slots.sparkline
    ? {
        data: slots.sparkline?.data ?? [],
        showTicks: slots.sparkline?.showTicks ?? config.sparkline?.showTicks,
        showContextValues:
          slots.sparkline?.showContextValues ??
          config.sparkline?.showContextValues,
        variant: slots.sparkline?.variant ?? config.sparkline?.variant,
        color: slots.sparkline?.color ?? config.sparkline?.color,
        gapPolicy: slots.sparkline?.gapPolicy ?? config.sparkline?.gapPolicy,
        thresholds: config.sparkline?.thresholds,
        xAxis: slots.sparkline?.xAxis,
        yAxis: slots.sparkline?.yAxis,
        curve: config.sparkline?.curve,
      }
    : undefined;
  const singleValueRef = useRef<HTMLDivElement>(null);

  const ref = useMergeRefs<SingleValueRef | HTMLDivElement>([
    forwardedRef,
    singleValueRef,
  ]);

  useImperativeHandle(forwardedRef, () => ({
    getConfig: () => {
      return JSON.stringify({
        type: ChartType.SingleValue,
        ...normalizeConfig(config),
      });
    },
  }));

  return (
    <div
      role="img"
      className={clsx(container, customerClassNames)}
      style={customerStyle}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={ref}
      aria-label={SINGLE_VALUE_ARIA_LABEL}
    >
      <SingleValueRenderer
        data={data}
        prefixIcon={prefixIcon}
        {...config}
        sparklineProps={sparklineProps}
        sparklineChildren={sparklineChildren}
        trendProps={slots.trend}
        emptyState={emptyState}
        errorState={errorState}
        loading={loading}
        applyThresholdBackground={applyThresholdBackground}
      >
        {children}
      </SingleValueRenderer>
    </div>
  );
});

(
  SingleValueRoot as typeof SingleValueRoot & { displayName: string }
).displayName = 'SingleValue';

/**
 * The `SingleValue` is a simple and concise representation of a single data point
 * or metric , which can either be a text or a number.
 * @public
 */
export const SingleValue = Object.assign(SingleValueRoot, {
  Sparkline,
  Trend,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
});
